import {
  addAlignmentReferences,
  addMapWorkspaceReferences,
  addMobileMappingMissionReferences,
  addModelReferences,
  addTerrestrialScanReferences,
  deleteReference,
  listReferences,
  type ReferenceType,
} from '@data-access/geospatial/fx-workspace-service';
import { throwIfError } from '@trimble/http-utils';

import {
  DEFAULT_PAGE_SIZE,
  HTTP_NOT_FOUND,
  fetchAllPages,
  getClientHeaders,
  unwrapOrThrow,
  type ApiFxWorkspaceRepositoryConfig,
} from './api-fx-workspace-repository.helpers';
import type { SyncFxWorkspaceReferencesInput } from './fx-workspace-repository';

const addReferencesByType = {
  alignment: addAlignmentReferences,
  mapWorkspace: addMapWorkspaceReferences,
  mobileMappingMission: addMobileMappingMissionReferences,
  model: addModelReferences,
  terrestrialScan: addTerrestrialScanReferences,
} as const satisfies Record<
  ReferenceType,
  typeof addMobileMappingMissionReferences
>;

export async function listFxWorkspaceReferences(
  config: ApiFxWorkspaceRepositoryConfig,
  workspaceTrn: string
): Promise<readonly string[]> {
  const tenantTrn = config.getTenantTrn();
  if (tenantTrn === undefined) {
    return [];
  }

  const references = await fetchAllPages(async (pageIndex) => {
    const response = await listReferences({
      client: config.client,
      path: { tenantTrn, workspaceTrn },
      headers: getClientHeaders(config.clientName, config.clientVersion),
      query: {
        pageIndex,
        pageSize: DEFAULT_PAGE_SIZE,
      },
    });

    return unwrapOrThrow(response, 'Failed to list FX workspace references');
  });

  return references.map((reference) => reference.refTrn);
}

export async function addFxWorkspaceReference(
  config: ApiFxWorkspaceRepositoryConfig,
  workspaceTrn: string,
  refTrn: string,
  type: ReferenceType
): Promise<void> {
  const tenantTrn = config.getTenantTrn();
  if (tenantTrn === undefined) {
    throw new Error('Tenant TRN is required to add FX workspace reference');
  }

  const response = await addReferencesByType[type]({
    client: config.client,
    path: { tenantTrn, workspaceTrn },
    body: { refTrns: [refTrn] },
    headers: getClientHeaders(config.clientName, config.clientVersion),
  });

  unwrapOrThrow(response, 'Failed to add FX workspace reference');
}

export async function syncFxWorkspaceReferences(
  config: ApiFxWorkspaceRepositoryConfig,
  workspaceTrn: string,
  input: SyncFxWorkspaceReferencesInput
): Promise<void> {
  const { toAdd, toRemove } = input;

  // Apply removals first so a failed add cannot leave refs that should
  // already be gone.
  for (const refTrn of toRemove) {
    await removeFxWorkspaceReference(config, workspaceTrn, refTrn);
  }

  for (const add of toAdd) {
    await addFxWorkspaceReference(config, workspaceTrn, add.refTrn, add.type);
  }
}

export async function removeFxWorkspaceReference(
  config: ApiFxWorkspaceRepositoryConfig,
  workspaceTrn: string,
  refTrn: string
): Promise<void> {
  const tenantTrn = config.getTenantTrn();
  if (tenantTrn === undefined) {
    throw new Error('Tenant TRN is required to remove FX workspace reference');
  }

  const response = await deleteReference({
    client: config.client,
    path: { tenantTrn, workspaceTrn, refTrn },
    headers: getClientHeaders(config.clientName, config.clientVersion),
  });

  // Deleting a reference is idempotent: a 404 means it is already absent,
  // which is the desired end state, so treat it as success.
  if (response.response?.status === HTTP_NOT_FOUND) {
    return;
  }

  throwIfError(response, 'Failed to remove FX workspace reference');
}
